/* ==========================================================================
   HLF Media Showcase — header video controller

   A <video> cannot pick a source by media query the way <picture> can: the
   `media` attribute on <source> is ignored inside <video>. Rendering one video
   per breakpoint and hiding the other with CSS would still hand the browser
   two files to fetch.

   So both slots ship with an empty <video preload="none"> carrying its URL in
   data-src, and this controller attaches the source to whichever slot the
   current breakpoint actually shows. The other stays empty, and is emptied
   again if the viewport crosses the breakpoint.
   ========================================================================== */
(function () {
  'use strict';

  var BREAKPOINT = '(min-width: 750px)';

  function HlfMediaShowcase(root) {
    this.root = root;
    this.videos = Array.prototype.slice.call(
      root.querySelectorAll('[data-hlf-showcase-video]')
    );
    if (!this.videos.length) return;

    this.autoplay = root.dataset.videoAutoplay === 'true';
    this.mql = window.matchMedia(BREAKPOINT);

    // Honouring the OS setting matters more than the merchant's autoplay
    // preference — motion sensitivity is an accessibility need, not a taste.
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.onBreakpointChange = this.sync.bind(this);
    this.addBreakpointListener();

    this.observeVisibility();
    this.sync();
  }

  HlfMediaShowcase.prototype.addBreakpointListener = function () {
    if (this.mql.addEventListener) {
      this.mql.addEventListener('change', this.onBreakpointChange);
    } else if (this.mql.addListener) {
      // Safari < 14
      this.mql.addListener(this.onBreakpointChange);
    }
  };

  /* Which slot is on screen right now, per the same rule the CSS uses. */
  HlfMediaShowcase.prototype.isActive = function (video) {
    var slot = video.closest('.hlf-showcase__slot');
    if (!slot) return false;
    var isDesktopSlot = slot.classList.contains('hlf-showcase__slot--desktop');
    return this.mql.matches ? isDesktopSlot : !isDesktopSlot;
  };

  HlfMediaShowcase.prototype.activate = function (video) {
    var src = video.dataset.src;
    if (!src) return;

    if (video.getAttribute('src') !== src) {
      video.setAttribute('src', src);
      video.load();
    }

    if (this.autoplay && !this.reduceMotion) {
      // Autoplay rejects on some browsers regardless of muted; the poster
      // stays up in that case, which is an acceptable resting state.
      var played = video.play();
      if (played && typeof played.catch === 'function') {
        played.catch(function () {});
      }
    }
  };

  HlfMediaShowcase.prototype.deactivate = function (video) {
    video.pause();
    if (video.hasAttribute('src')) {
      // Detaching the source releases the buffer the hidden slot was holding.
      video.removeAttribute('src');
      video.load();
    }
  };

  HlfMediaShowcase.prototype.sync = function () {
    var self = this;
    this.videos.forEach(function (video) {
      if (self.isActive(video)) {
        self.activate(video);
      } else {
        self.deactivate(video);
      }
    });
  };

  /* Playing a video nobody can see wastes battery and data. */
  HlfMediaShowcase.prototype.observeVisibility = function () {
    if (!('IntersectionObserver' in window)) return;

    var self = this;
    this.observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          self.videos.forEach(function (video) {
            if (!self.isActive(video)) return;
            if (entry.isIntersecting) {
              if (self.autoplay && !self.reduceMotion) {
                var played = video.play();
                if (played && typeof played.catch === 'function') {
                  played.catch(function () {});
                }
              }
            } else {
              video.pause();
            }
          });
        });
      },
      { rootMargin: '100px 0px' }
    );

    this.observer.observe(this.root);
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-hlf-showcase]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.hlfShowcase) return;
      root.hlfShowcase = new HlfMediaShowcase(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor re-renders a section's markup on every settings change, so
  // the new nodes need wiring up again.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
